import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CartFooter from '@nykaa/cart-shared/components/Footer';
import DiscountTooltip from '@nykaa/cart-shared/wrapper/DiscountTooltip';
import { CART_TEXT_MESSAGES } from '@nykaa/cart-shared/constants/message';
import {
  getFirstErrorProduct,
  getOOSAndDisabledAndDiscontinuedProducts,
  scrollToElement,
} from '@nykaa/cart-shared/helpers/utils';
import {
  deviceTypes,
  EDD_NOT_ITEM_SERVICABLE_ERROR_CODE,
} from '@nykaa/cart-shared/constants';
import { setToastMessage } from '@nykaa/cart-shared/store/action';
import { logger } from '@nykaa/logger';
import { getAnalyticsData } from '@nykaa/cart-shared/helpers/tracking';
import { CartEvents } from '@nykaa/cart-shared/constants/tracking';
import { pushData } from '@nykaa/data-layer';
import {
  DiscountTooltipWrapper,
  FooterLayout,
} from 'layouts/ShoppingBag/styled';
import { State } from 'store/types';
import { useIsLoggedIn } from 'hooks/auth';
import { pages } from 'constants/urls';
import { AB_FOOTER_CTA_TEXT } from 'constants/experiments';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { gotoUrl } from 'shared/helpers/shoppingBag';

interface Props {
  toggleShowOOSBottomSheet: () => void;
  isConvenienceFeeEnabled?: boolean;
  grandTotalIconClickHandler: () => void;
}

const Footer = ({
  toggleShowOOSBottomSheet,
  isConvenienceFeeEnabled,
  grandTotalIconClickHandler,
}: Props) => {
  const dispatch = useDispatch();
  const cartInfo = useSelector((state: State) => state.cartReducer.cartInfo);
  const { freeSampleData, items, addressDTO, cartPriceDTO, error } =
    cartInfo || {};
  const isLoggedIn = useIsLoggedIn();
  const { cartDesignUpdate: isNewDesignEnabled, cartShowSavingStrip } =
    useSelector((state: State) => state.remoteConfig.switches) || false;
  const isCTATextV2Enabled =
    useSelector((state: State) =>
      getEligibleForVariant(state, AB_FOOTER_CTA_TEXT, 'A')
    ) || false;
  const {
    GRAND_TOTAL,
    PROCEED,
    WITHOUT_FREEBIES,
    PROCEED_TO_PAY,
    ADD_ADDRESS,
  } = CART_TEXT_MESSAGES;
  const { grandTotal, bagTotal } = cartPriceDTO || {};

  const errorItems = getOOSAndDisabledAndDiscontinuedProducts(items);
  const topErrorItemForScroll = getFirstErrorProduct(items, [
    EDD_NOT_ITEM_SERVICABLE_ERROR_CODE,
  ]);

  const proceedBtnText = useMemo(() => {
    let text = PROCEED;
    if (isCTATextV2Enabled) {
      text = PROCEED_TO_PAY;
      if (!addressDTO) {
        text = ADD_ADDRESS;
      }
    }

    return text;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCTATextV2Enabled, addressDTO]);

  const freeBiesText = useMemo(() => {
    let text = '';
    if ((freeSampleData?.eligibleCount || 0) > 0) {
      text = WITHOUT_FREEBIES;
      if (freeSampleData?.eligibleCount == freeSampleData?.claimedCount) {
        text = '';
      }
    }

    if (isCTATextV2Enabled) {
      text = '';
    }
    return text;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freeSampleData]);

  const onProceed = () => {
    try {
      if (errorItems.length > 0) {
        if (!isLoggedIn) {
          gotoUrl(pages.CHECKOUT_AUTH_PAGE);
          return;
        }
        toggleShowOOSBottomSheet();
        return;
      } else if (topErrorItemForScroll) {
        const item = document.getElementById(
          `${topErrorItemForScroll?.productId}`
        );
        scrollToElement(item);
        return;
      }

      if (error?.errorMessage) {
        setToastMessage(error?.errorMessage)(dispatch);
        return;
      }

      const trackingData = getAnalyticsData(CartEvents.PROCEED, {
        proceedBtnText,
        ...cartInfo,
        isConvenienceFeeEnabled,
      });
      pushData(trackingData.gtmData);
      gotoUrl(pages.CHECKOUT_AUTH_PAGE);
    } catch (e) {
      logger.error(e, 'Error occurred while proceeding to checkout');
    }
  };
  const config = {
    grandTotalText: GRAND_TOTAL,
    grandTotalValue: `₹${grandTotal}`,
    showGrandTotalIcon: true,
    bagTotalValue: bagTotal,
    proceedBtnTitle: proceedBtnText,
    proceedBtnSubtitle: freeBiesText,
    grandTotalIconClickHandler,
    proceedBtnClickHandler: onProceed,
    showFooterV2: isNewDesignEnabled,
    overrides: {
      style: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
      },
    },
  };

  return (
    <FooterLayout data-test-id="footer">
      {cartShowSavingStrip && (
        <DiscountTooltipWrapper>
          <DiscountTooltip
            cartInfo={cartInfo}
            deviceType={deviceTypes.DESKTOP}
          />
        </DiscountTooltipWrapper>
      )}
      <CartFooter {...config} />
    </FooterLayout>
  );
};

export default Footer;
